import React, { useEffect, useState } from 'react'
import { connect, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'

import { Close } from '@material-ui/icons'
import { Box, Grid, IconButton, Modal, Paper, Tooltip, withStyles } from '@material-ui/core'

import { CleanButton, Label, SelectField, SelectList, Loader, Alert, PrimaryButton } from '../default'
import { acClearAddedPlans, acGetPlanTables, acGetValidPlans, acPostAddPlanToPlanTable } from '../../class/actions'

function InsertPlanModal({
  classes, open, onClose,
  loading, fetchError,
  planTables, planTablesError, loadingPlanTables,
  plans, loadingPlans
}) {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [selectedPlan, setSelectedPlan] = useState('')
  const [selectedPlanError, setSelectedPlanError] = useState(false)
  const [error, setError] = useState(null)
  const [planTablesIncluded, setPlanTablesIncluded] = useState([null])

  useEffect(() => {
    dispatch(acGetPlanTables())
    dispatch(acClearAddedPlans())
  }, [dispatch])

  useEffect(() => {
    if (!plans)
      dispatch(acGetValidPlans())
  }, [dispatch, plans])

  const insertPlans = () => {
    if (!selectedPlan) {
      setSelectedPlanError(true)
      return
    }

    if (planTablesIncluded.filter(planTable => planTable !== null).length && planTablesIncluded.length > new Set(planTablesIncluded.map(planTable => planTable ? planTable.plan_table_id : -1)).size) {
      setError(t('modals.plans.create.errorRepeatedPlanTables'))
      return
    }

    setError(null)

    Promise.all(planTablesIncluded.filter(planTable => planTable !== null).map(newPlanTable => {
      return new Promise((resolve, reject) => {
        dispatch(acPostAddPlanToPlanTable(newPlanTable.plan_table_id, selectedPlan, newPlanTable.price))
          .then(value => resolve(value))
          .catch(err => reject(err))
      })
    }))
      .then(() => onClose())
      .catch(err => {
        console.log(err)
        setError(t('modals.plans.create.unknownError'))
      })
  }

  const handleSelectListChange = data => {
    setPlanTablesIncluded(data)
  }

  return (
    <Modal open={open} onClose={onClose} disableAutoFocus style={{ outline: 0, overflowY: 'scroll' }}>
      <Paper
        square={true}
        className={classes.root + ' ' + classes.modalDiv}
      >
        <Box display='flex' alignItems='center' justifyContent='space-between'>
          {
            (loadingPlanTables || loadingPlans || !plans) ?
              <Loader /> : (
                <Grid container spacing={2} className={classes.grid}>

                  <Grid item xs={8}>
                    <Label v='h6'>
                      {t('modals.plans.insert.title')}
                    </Label>
                  </Grid>
                  <Grid item xs={4} style={{ textAlign: 'right', padding: 0 }}>
                    <Tooltip title={t('default.close')}>
                      <IconButton onClick={onClose}>
                        <Close />
                      </IconButton>
                    </Tooltip>
                  </Grid>

                  <Grid item xs={7}>
                    <SelectField
                      label={t('modals.plans.insert.plan')}
                      items={plans.map(plan => ({
                        name: plan.name,
                        value: plan.plan_id
                      }))}
                      value={selectedPlan}
                      onChange={e => {
                        setSelectedPlan(e.target.value)
                        setSelectedPlanError(false)
                      }}
                      error={selectedPlanError}
                      required
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Label bold>
                          {t('modals.plans.addToPlanTables')}
                        </Label>
                      </Grid>
                      <Grid item xs={12}>
                        <SelectList
                          label={t('modals.plans.planTable')}
                          selectItems={planTables.map(planTable => ({
                            name: planTable.name,
                            value: planTable.plan_table_id
                          }))}
                          extraDataKey='price'
                          valueKey='plan_table_id'
                          extraDataDefaultValue={0}
                          extraDataParseFunction={value => parseFloat(value)}
                          onChange={handleSelectListChange}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              )
          }
        </Box>

        <div>
          <div style={{ marginTop: '15px' }}>
            {
              (fetchError) ? (
                <Alert style={{ marginBottom: '15px' }} noMargin color="error" text={fetchError.description ? fetchError.description : t('modals.plans.create.unknownFetchError')} />
              )
                : (planTablesError) ? (
                  <Alert style={{ marginBottom: '15px' }} noMargin color="error" text={planTablesError.description ? planTablesError.description : t('modals.plans.create.unknownPlanTableError')} />
                )
                  : error ?
                    <Alert style={{ marginBottom: '15px' }} noMargin color="error" text={error} /> : null
            }
          </div>
          <div style={{ marginTop: '15px' }}>
            <Grid container style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <CleanButton onClick={onClose} style={{ marginRight: '15px' }}>
                {t('default.cancel')}
              </CleanButton>
              <PrimaryButton onClick={insertPlans}>
                {
                  (loading || loadingPlanTables) ? (
                    <Loader />
                  ) : (
                    <div>
                      {t('modals.plans.insertPlan')}
                    </div>
                  )
                }
              </PrimaryButton>
            </Grid>
          </div>
        </div>
      </Paper>
    </Modal>
  )
}

InsertPlanModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  close: PropTypes.func
}

InsertPlanModal.defaultProps = {
  open: false,
  onClose: () => true,
  close: () => true
}

const styles = theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    position: 'absolute',
    padding: theme.spacing(2),
  },
  grid: {
    display: 'flex',
    flex: 1
  },
  modalDiv: {
    [theme.breakpoints.up('md')]: {
      width: '50%'
    },
    width: '100%',
    top: '10%',
    left: '50%',
    transform: 'translate(-50%, -10%)',
  }
})

const mapStateToProps = ({ ReducerFinancial, ReducerPlanTable, ReducerPlan }) => ({
  loading: ReducerFinancial.loading,
  fetchError: ReducerFinancial.error,
  payment: ReducerFinancial.payment,

  planTables: ReducerPlanTable.planTables,
  planTablesError: ReducerPlanTable.planTablesError,
  loadingPlanTables: ReducerPlanTable.loadingPlanTables,

  plans: ReducerPlan.plans,
  plansError: ReducerPlan.plansError,
  loadingPlans: ReducerPlan.loadingPlans
})

export default connect(mapStateToProps)(withStyles(styles)(InsertPlanModal))